import React, { useCallback, useState } from 'react';
import Card from '../../../ui/Card';
import { UnitTable } from './UnitTable';
import type { Floorplan, Unit } from './UnitTable';
import { PhotoLightbox } from '../MediaCenter/PhotoLightbox';
import * as styles from './unitsModule.styles';

const MAX_PREVIEW_ROWS = 5;

type FloorPlanCardProps = {
  floorplan: Floorplan;
  onUnitSelect: (unit: Unit, floorplan: Floorplan) => void;
  onSeeAll: (floorplan: Floorplan) => void;
  compact?: boolean;
};

const getFloorplanImageUrl = (floorplan: Floorplan): string | null => {
  const layoutCount = floorplan.layoutCount ?? 0;
  if (layoutCount === 0) return null;

  if (floorplan.largePhotos?.length > 0) return floorplan.largePhotos[0].url;
  if (floorplan.photos?.length > 0) return floorplan.photos[0].url;
  return null;
};

const getBedroomLabel = (beds: number): string => {
  if (beds === 0) return 'Studio';
  if (beds === 1) return '1 Bed';
  return `${beds} Beds`;
};

const FloorPlanPlaceholderIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <rect x="4" y="4" width="24" height="24" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <line x1="4" y1="16" x2="28" y2="16" stroke="currentColor" strokeWidth="1.5" />
    <line x1="16" y1="4" x2="16" y2="28" stroke="currentColor" strokeWidth="1.5" />
    <line x1="16" y1="16" x2="16" y2="28" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2" />
    <rect x="8" y="8" width="4" height="4" rx="0.5" stroke="currentColor" strokeWidth="1" fill="none" />
  </svg>
);

const NoFloorPlanPlaceholder = () => (
  <div className={styles.floorPlanImageOuter}>
    <div className={styles.noFloorPlanPlaceholder}>
      <FloorPlanPlaceholderIcon />
      <span className={styles.placeholderText}>No floor plan yet</span>
      <span className={styles.placeholderSubtext}>Yeah, we wish we had it too.</span>
    </div>
  </div>
);

const FloorPlanSummary = ({ floorplan }: { floorplan: Floorplan }) => {
  const parts: Array<string> = [];
  parts.push(getBedroomLabel(floorplan.beds));
  if (floorplan.baths) parts.push(`${floorplan.baths} bath`);
  if (floorplan.sqft) parts.push(`${floorplan.sqft.toLocaleString('en-US')} sqft`);

  return (
    <p className={styles.floorPlanSummary}>
      {parts.map((part, i) => (
        <React.Fragment key={part}>
          {i > 0 && <span className={styles.floorPlanSummarySeparator}> {'\u2022'} </span>}
          {part}
        </React.Fragment>
      ))}
    </p>
  );
};

export const FloorPlanCard = React.memo(({ floorplan, onUnitSelect, onSeeAll, compact }: FloorPlanCardProps) => {
  const imageUrl = getFloorplanImageUrl(floorplan);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const handleImageClick = useCallback(() => {
    setLightboxOpen(true);
  }, []);

  const handleSeeAll = useCallback(() => {
    onSeeAll(floorplan);
  }, [onSeeAll, floorplan]);

  const lightboxPhotos = imageUrl ? [{ url: imageUrl, caption: `Floor plan ${floorplan.name || ''}` }] : [];

  return (
    <Card variant="default" size="md" className={compact ? styles.compactCard : styles.baseCard}>
      {imageUrl ? (
        <div className={styles.floorPlanImageOuter}>
          <div className={compact ? styles.compactImageWrapper : styles.floorPlanImageWrapper}>
            <button
              type="button"
              className={styles.floorPlanImageButton}
              onClick={handleImageClick}
              aria-label={`View floor plan ${floorplan.name || ''}`}
            >
              <img src={imageUrl} alt={`Floor plan ${floorplan.name || ''}`} className={styles.floorPlanImage} />
            </button>
          </div>
        </div>
      ) : (
        <NoFloorPlanPlaceholder />
      )}

      <div className={styles.cardContent}>
        <FloorPlanSummary floorplan={floorplan} />

        <div className={compact ? styles.compactUnitTableWrapper : undefined}>
          <UnitTable
            units={floorplan.units}
            floorplan={floorplan}
            onUnitSelect={onUnitSelect}
            maxRows={MAX_PREVIEW_ROWS}
            onSeeAll={handleSeeAll}
          />
        </div>
      </div>

      {lightboxPhotos.length > 0 && (
        <PhotoLightbox photos={lightboxPhotos} initialIndex={0} open={lightboxOpen} onOpenChange={setLightboxOpen} />
      )}
    </Card>
  );
});
